import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useRepFilter } from "@/context/RepFilterContext";
import { ChevronLeft, ChevronRight, ClipboardList, CalendarClock, ListTodo, Calendar as CalendarIcon } from "lucide-react";

const DAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

const KIND = {
  visit:    { dot: "bg-sky-500",     chipBg: "bg-sky-500",     chipSoft: "bg-sky-100 text-sky-900",     icon: ClipboardList },
  followup: { dot: "bg-orange-500",  chipBg: "bg-orange-500",  chipSoft: "bg-orange-100 text-orange-900", icon: CalendarClock },
  task:     { dot: "bg-emerald-600", chipBg: "bg-emerald-600", chipSoft: "bg-emerald-100 text-emerald-900", icon: ListTodo },
};

const PRIO_TONE = {
  high: "bg-rose-100 text-rose-800 border-rose-200",
  medium: "bg-amber-100 text-amber-800 border-amber-200",
  low: "bg-slate-100 text-slate-700 border-slate-200",
};

function ymOf(d) { return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`; }
function iso(d) { return d ? `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}` : ""; }
function isSameDay(a, b) { return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate(); }

export default function DashboardCalendar() {
  const { repId } = useRepFilter();
  const [cursor, setCursor] = useState(() => { const d = new Date(); d.setDate(1); return d; });
  const [events, setEvents] = useState([]);
  const [selected, setSelected] = useState(new Date());

  useEffect(() => {
    api.get("/dashboard/calendar", { params: { month: ymOf(cursor), rep_id: repId !== "all" ? repId : undefined } }).then(({ data }) => setEvents((data.events || []).filter(e => e.kind !== "plan")));
  }, [cursor, repId]);

  const grid = useMemo(() => {
    const first = new Date(cursor);
    const startWeekday = first.getDay();
    const days = new Date(first.getFullYear(), first.getMonth() + 1, 0).getDate();
    const cells = [];
    for (let i = 0; i < startWeekday; i++) cells.push(null);
    for (let d = 1; d <= days; d++) cells.push(new Date(first.getFullYear(), first.getMonth(), d));
    while (cells.length % 7 !== 0) cells.push(null);
    return cells;
  }, [cursor]);

  const eventsByDay = useMemo(() => {
    const map = {};
    events.forEach((e) => { (map[e.date] = map[e.date] || []).push(e); });
    return map;
  }, [events]);

  const totals = useMemo(() => ({
    visit: events.filter((e) => e.kind === "visit").length,
    followup: events.filter((e) => e.kind === "followup").length,
    task: events.filter((e) => e.kind === "task").length,
  }), [events]);

  const todaysList = selected ? (eventsByDay[iso(selected)] || []) : [];
  const monthLabel = cursor.toLocaleString(undefined, { month: "long", year: "numeric" });

  return (
    <Card className="border-slate-200 shadow-none overflow-hidden" data-testid="dashboard-calendar">
      {/* Gradient header */}
      <div className="p-6 text-white" style={{ background: "linear-gradient(135deg, var(--hero-from) 0%, var(--hero-via) 55%, var(--hero-to) 100%)" }}>
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <div className="text-[11px] uppercase tracking-widest font-mono-plex text-white/70 flex items-center gap-1.5"><CalendarIcon className="w-3 h-3" /> Calendar</div>
            <h3 className="font-heading font-black text-3xl mt-1">{monthLabel}</h3>
          </div>
          <div className="flex items-center gap-1">
            <Button variant="ghost" size="icon" onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() - 1, 1))} data-testid="cal-prev" className="text-white hover:bg-white/15"><ChevronLeft className="w-4 h-4" /></Button>
            <Button variant="ghost" size="sm" onClick={() => { const d = new Date(); d.setDate(1); setCursor(d); setSelected(new Date()); }} data-testid="cal-today" className="text-white hover:bg-white/15 border border-white/30">Today</Button>
            <Button variant="ghost" size="icon" onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1))} data-testid="cal-next" className="text-white hover:bg-white/15"><ChevronRight className="w-4 h-4" /></Button>
          </div>
        </div>
        <div className="mt-4 flex flex-wrap gap-2">
          <div className="rounded-full bg-white/15 backdrop-blur-md px-3 py-1 text-xs font-mono-plex flex items-center gap-2"><span className="w-1.5 h-1.5 rounded-full bg-sky-300" /> {totals.visit} visits</div>
          <div className="rounded-full bg-white/15 backdrop-blur-md px-3 py-1 text-xs font-mono-plex flex items-center gap-2"><span className="w-1.5 h-1.5 rounded-full bg-orange-300" /> {totals.followup} follow-ups</div>
          <div className="rounded-full bg-white/15 backdrop-blur-md px-3 py-1 text-xs font-mono-plex flex items-center gap-2"><span className="w-1.5 h-1.5 rounded-full bg-emerald-300" /> {totals.task} tasks</div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 p-6">
        <div className="lg:col-span-2">
          <div className="grid grid-cols-7 gap-1 text-[10px] uppercase tracking-widest font-mono-plex text-slate-500 mb-1">
            {DAYS.map((d, i) => <div key={d} className={`text-center py-1 ${i === 0 || i === 6 ? "text-rose-500" : ""}`}>{d}</div>)}
          </div>
          <div className="grid grid-cols-7 gap-1">
            {grid.map((d, i) => {
              if (!d) return <div key={i} className="h-24 rounded-md bg-slate-50/40" />;
              const key = iso(d);
              const dayEvents = eventsByDay[key] || [];
              const isToday = isSameDay(d, new Date());
              const isSel = selected && isSameDay(d, selected);
              const isWeekend = d.getDay() === 0 || d.getDay() === 6;
              return (
                <button
                  key={i}
                  onClick={() => setSelected(d)}
                  className={`h-24 rounded-md border p-1.5 text-left transition-all duration-200 hover:-translate-y-[1px] ${
                    isSel
                      ? "border-transparent text-white shadow-lg"
                      : `border-slate-200 hover:border-indigo-300 ${isWeekend ? "bg-rose-50/40" : "bg-white"}`
                  } ${isToday && !isSel ? "ring-2 ring-amber-400 ring-offset-1" : ""}`}
                  style={isSel ? { background: "linear-gradient(135deg, var(--hero-from), var(--hero-via))" } : undefined}
                  data-testid={`cal-day-${key}`}
                >
                  <div className={`text-xs font-bold ${isSel ? "text-white" : isToday ? "text-amber-600" : "text-slate-900"}`}>{d.getDate()}</div>
                  <div className="flex flex-col gap-0.5 mt-1">
                    {dayEvents.slice(0, 2).map((e, ei) => {
                      const tone = KIND[e.kind] || KIND.task;
                      return (
                        <div key={ei} className={`text-[9px] rounded px-1 py-0.5 truncate leading-tight ${isSel ? "bg-white/20 text-white" : tone.chipSoft}`} title={e.title}>
                          {e.title}
                        </div>
                      );
                    })}
                    {dayEvents.length > 2 && <span className={`text-[9px] font-mono-plex ${isSel ? "text-white/70" : "text-slate-500"}`}>+{dayEvents.length - 2} more</span>}
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        <div className="lg:border-l border-slate-100 lg:pl-4">
          <div className="rounded-lg bg-gradient-to-br from-slate-900 to-slate-800 text-white p-4 mb-3">
            <div className="text-[10px] uppercase tracking-widest font-mono-plex text-white/70">Selected day</div>
            <div className="font-heading font-black text-xl mt-0.5">{selected ? selected.toLocaleDateString(undefined, { weekday: "long" }) : "—"}</div>
            <div className="text-sm text-white/80 font-mono-plex mt-0.5">{selected ? selected.toLocaleDateString(undefined, { month: "long", day: "numeric", year: "numeric" }) : ""}</div>
          </div>
          <div className="space-y-2 max-h-[380px] overflow-y-auto pr-1">
            {todaysList.length === 0 && (
              <div className="text-xs text-slate-400 italic py-8 text-center border border-dashed border-slate-200 rounded-md">No events on this day.</div>
            )}
            {todaysList.map((e, i) => {
              const tone = KIND[e.kind] || KIND.task;
              const Icon = tone.icon;
              return (
                <div key={`${e.kind}-${e.id}-${i}`} className={`rounded-md p-2.5 row-enter border-l-4 ${tone.chipSoft} border-l-current`} data-testid={`cal-event-${e.kind}-${e.id}`}>
                  <div className="flex items-start gap-2">
                    <div className={`w-7 h-7 rounded-md flex items-center justify-center ${tone.chipBg} text-white shrink-0`}><Icon className="w-3.5 h-3.5" /></div>
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-medium text-slate-900">{e.title}</div>
                      <div className="text-[11px] text-slate-600 mt-0.5 flex gap-2 flex-wrap items-center">
                        {e.facility_name && <span className="truncate">{e.facility_name}</span>}
                        {e.owner_name && <span>· {e.owner_name}</span>}
                        {e.priority && <Badge variant="outline" className={`text-[9px] py-0 h-4 ${PRIO_TONE[e.priority] || ""}`}>{e.priority}</Badge>}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </Card>
  );
}
